import React, { useRef } from 'react';
import { IonModal, IonContent, IonButton } from '@ionic/react';
import {
  INTERSTITIAL_BREAKPOINT,
  INTERSTITIAL_BREAKPOINTS,
} from '@/modules/config';
import '@/components/Interstitial.css';

interface PrivateInviteInterstitialProps {
  isOpen: boolean;
  topicType: string;
  senderName?: string;
  // When the recipient already has a phone on their profile, the ask is to
  // *verify* that phone rather than *link* a new one — the copy, button label,
  // and detail heading switch to the verify wording.
  hasPhone?: boolean;
  // Jump to the start of the Profile Wizard so the recipient can link their phone.
  onLinkPhone: () => void;
  onDismiss: () => void;
}

/**
 * Shown to a guest who followed a by-invitation-only invite link. Unlike the
 * WelcomeInterstitial (public topics), the recipient can't see topic details
 * until they prove who they are by linking the phone the invite was sent to.
 *
 * Intentionally NOT suppressible: linking the phone is required to do anything
 * with the invited item, so there is no "don't show again" affordance.
 */
const PrivateInviteInterstitial: React.FC<PrivateInviteInterstitialProps> = ({
  isOpen,
  topicType,
  senderName,
  hasPhone = false,
  onLinkPhone,
  onDismiss,
}) => {
  const modalRef = useRef<HTMLIonModalElement>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);

  const expandToAnchor = async (anchorId: string) => {
    await modalRef.current?.setCurrentBreakpoint(1);
    setTimeout(() => {
      const el = document.getElementById(anchorId);
      if (el) {
        contentRef.current?.scrollToPoint(0, el.offsetTop - 16, 300);
      }
    }, 300);
  };

  const displayName =
    senderName && senderName !== '_' ? senderName : 'A contact';

  const topicPhrase = hasPhone
    ? 'verify your phone with Snowball'
    : 'link your phone with a Snowball profile';
  const buttonLabel = hasPhone ? 'Verify' : 'Link phone number';
  const whyHeading = hasPhone ? 'Why verify?' : 'Why link?';

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      initialBreakpoint={INTERSTITIAL_BREAKPOINT}
      breakpoints={INTERSTITIAL_BREAKPOINTS}
      handle
      onDidDismiss={onDismiss}
    >
      <IonContent ref={contentRef} className="ion-padding-top">
        <p className="interstitial__message">
          <strong>{displayName}</strong> shared this private {topicType} through
          Snowball, a web app that makes it easy to spread the word. In order to
          see details, you'll need to {topicPhrase}.{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            (
            <button
              className="interstitial__link"
              onClick={() => expandToAnchor('private-invite-convince')}
            >
              convince me
            </button>
            )
          </span>
        </p>

        <IonButton
          expand="block"
          className="interstitial__continue-btn"
          onClick={onLinkPhone}
        >
          {buttonLabel}
        </IonButton>

        {/* Detail zone — only visible when sheet is expanded */}
        <hr className="interstitial__section-rule" />

        <div id="private-invite-convince">
          <p className="interstitial__section-heading">{whyHeading}</p>
          <p className="interstitial__body">
            This {topicType} wasn't meant for the general public, so we just
            want to double-check the invite reached the right person. It takes
            less than 30 seconds to verify your phone number. Plus, then you'll
            be ready to do your own topic sharing and have Snowball keep stats
            on your impact.
          </p>
        </div>

        {/* Bottom padding so the last section isn't flush against the edge */}
        <div style={{ height: 40 }} />
      </IonContent>
    </IonModal>
  );
};

export default PrivateInviteInterstitial;
